import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Search, TrendingUp, TrendingDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const PAGE = 24;

const StockCell = ({ s }) => (
  <Link to={`/stock/${s.symbol}`} className="flex items-center justify-between px-4 py-3 border-b border-r border-bt-border-subtle panel-hover"
    data-testid={`all-stock-${s.symbol}`}>
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-medium text-bt-text-primary">{s.symbol}</span>
        <span className="text-xs text-bt-text-tertiary truncate hidden sm:inline">{s.sector}</span>
      </div>
      <p className="text-xs text-bt-text-secondary truncate">{s.name}</p>
    </div>
    <div className="text-right shrink-0 ml-2">
      {s.price != null ? (
        <>
          <p className="font-mono text-sm text-bt-text-primary">${s.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <div className={`flex items-center justify-end gap-1 font-mono text-xs ${(s.change_pct || 0) >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
            {(s.change_pct || 0) >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {(s.change_pct || 0) >= 0 ? "+" : ""}{s.change_pct}%
          </div>
        </>
      ) : <p className="font-mono text-xs text-bt-text-tertiary">—</p>}
    </div>
  </Link>
);

export default function AllStocksPanel({ stocks }) {
  const [sector, setSector] = useState("All");
  const [q, setQ] = useState("");
  const [shown, setShown] = useState(PAGE);

  const sectors = useMemo(() => {
    const counts = {};
    stocks.forEach(s => { counts[s.sector] = (counts[s.sector] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [stocks]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return stocks.filter(s => (sector === "All" || s.sector === sector)
      && (!term || s.symbol.toLowerCase().includes(term) || s.name.toLowerCase().includes(term)));
  }, [stocks, sector, q]);

  const pick = (sec) => { setSector(sec); setShown(PAGE); };
  const tab = (active) => `shrink-0 px-2.5 py-1 text-[11px] font-mono border transition-colors ${active
    ? "border-bt-gold text-bt-gold bg-bt-gold/10" : "border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-primary"}`;

  return (
    <div className="md:col-span-3 lg:col-span-4 bg-bt-surface border border-bt-border-subtle" data-testid="all-stocks-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary">All Tracked Stocks</h2>
          <Badge variant="outline" className="text-xs font-mono border-bt-border-subtle text-bt-text-tertiary" data-testid="all-stocks-count">
            {filtered.length === stocks.length ? `${stocks.length} stocks` : `${filtered.length} of ${stocks.length}`}
          </Badge>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-bt-text-tertiary" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setShown(PAGE); }} placeholder="Filter by ticker or name"
            className="w-full h-8 pl-8 pr-2 text-xs font-mono bg-bt-surface-2 border border-bt-border-subtle text-bt-text-primary placeholder:text-bt-text-tertiary focus:outline-none focus:border-bt-border-focus"
            data-testid="all-stocks-search" />
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 px-4 pb-3" data-testid="all-stocks-sector-tabs">
        <button onClick={() => pick("All")} className={tab(sector === "All")} data-testid="all-stocks-tab-all">All</button>
        {sectors.map(([sec, n]) => (
          <button key={sec} onClick={() => pick(sec)} className={tab(sector === sec)}
            data-testid={`all-stocks-tab-${sec.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {sec} <span className="opacity-60">{n}</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-t border-bt-border-subtle">
        {filtered.slice(0, shown).map(s => <StockCell key={s.symbol} s={s} />)}
      </div>
      {filtered.length === 0 && <p className="px-4 py-6 text-center text-xs font-mono text-bt-text-tertiary" data-testid="all-stocks-empty">No stocks match.</p>}
      {filtered.length > shown && (
        <button onClick={() => setShown(n => n + PAGE * 2)} data-testid="all-stocks-show-more"
          className="w-full py-2.5 text-xs font-mono text-bt-gold hover:bg-bt-gold/5 transition-colors">
          Show more ({filtered.length - shown} remaining)
        </button>
      )}
    </div>
  );
}
